import React, { useState } from 'react'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { Colors, Shapes } from 'src/components/theme'
import { IconButton } from '@mui/material'
import { HelpOutline as HelpCircle } from '@emotion-icons/material/HelpOutline'
import { InsightCardLoadingBoundary } from '.'
import { HelpTooltip } from 'src/components/help-tooltip'
import { KeyboardArrowUp } from '@emotion-icons/material/KeyboardArrowUp'
import { KeyboardArrowDown } from '@emotion-icons/material/KeyboardArrowDown'
import { INSIGHT_WIDGET_PRINTABLE_CLASS } from 'src/atoms/report-insight'

type InsightCardProps = {
  id?: string
  title: string
  subtitle?: React.ReactNode
  left?: React.ReactNode
  right?: React.ReactNode
  subheader?: React.ReactNode
  children?: React.ReactNode
  errorTitle?: string
  errorMsg?: string
  noPadding?: boolean
  tooltipFile?: string
  noHeaderMargin?: boolean
  footerActionLabel?: string
  footer?: React.ReactNode
  containerPadding?: number
}

type InsightCardContentProps = {
  noPadding?: boolean
  flexDir?: 'row' | 'column'
  gap?: number
}

export type InsightCardWrapperProps = {
  isFooterOpen?: boolean
  padding?: number
}

const InsightCardPrintWrapper = styled.div``

export const InsightCardWrapper = styled.div<InsightCardWrapperProps>`
  width: 100%;
  background-color: ${Colors.bgWhite};
  border: 1px solid ${Colors.border};
  padding: 0;
  padding: ${(props: InsightCardWrapperProps) =>
    props.padding !== undefined ? `${props.padding}px` : `${Shapes.spacing[4]}px`};
  border-radius: ${(props: InsightCardWrapperProps) =>
    props.isFooterOpen ? `${Shapes.borderRadius}px ${Shapes.borderRadius}px 0 0` : Shapes.borderRadius}px;
  overflow: hidden;
`

export const InsightCardHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${Shapes.spacing[3]}px;
  margin-bottom: ${Shapes.spacing[6]}px;
`

export const InsightCardSubHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0 ${Shapes.spacing[3]}px;
  margin-bottom: ${Shapes.spacing[6]}px;

  & > * {
    margin-right: ${Shapes.spacing[3]}px;
  }
`

export const InsightCardHeaderLeft = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

export const InsightCardHeaderTitle = styled.div`
  margin-right: ${Shapes.spacing[3]}px;
`

export const InsightCardHeaderLeftExtra = styled.div`
  flex: 1;
  display: flex;
  & > * {
    margin-right: ${Shapes.spacing[3]}px;
  }
`

export const InsightCardHeaderRight = styled.div`
  min-height: 36px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

export const InsightCardHeaderRightBtn = styled(IconButton)``

export const InsightHelpCircleIcon = styled(HelpCircle)`
  width: 20px;
  height: 20px;
`

export const InsightCardHeaderRightExtra = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  & > * {
    margin-left: ${Shapes.spacing[3]}px;
  }
`

export const InsightCardContent = styled.div<InsightCardContentProps>`
  padding: 0 ${(props) => (props.noPadding ? 0 : `${Shapes.spacing[3]}px`)};
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: ${(props) => (props.flexDir ? props.flexDir : 'column')};
  gap: ${(props) => (props.gap ? `${props.gap}px` : 0)};
`

export const InsightCardWrapperForReport = styled.div<InsightCardWrapperProps>`
  width: 100%;
  background-color: ${Colors.bgWhite};
  border: 1px solid ${Colors.border};
  padding: 0;
  padding: ${(props: InsightCardWrapperProps) =>
    props.padding !== undefined ? `${props.padding}px` : `${Shapes.spacing[4]}px`};
  overflow: hidden;
  border-radius: 0px;
  margin: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`

export const TradeAreaWrapper = styled.div`
  margin: 0;
`

const InsightFooterAction = styled.div``

const InsightFooterActionBtn = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;

  & > * {
    margin-right: ${Shapes.spacing[1]}px;
  }
`

type InsightFooterProps = {
  isOpen: boolean
}

const InsightFooter = styled.div<InsightFooterProps>`
  background-color: ${Colors.bgDark};
  margin-top: -${Shapes.spacing[4]}px;
  margin-bottom: ${Shapes.spacing[4]}px;
  border-left: 1px solid ${(props: InsightFooterProps) => (props.isOpen ? Colors.border : 'transparent')};
  border-right: 1px solid ${(props: InsightFooterProps) => (props.isOpen ? Colors.border : 'transparent')};
  border-bottom: 1px solid ${(props: InsightFooterProps) => (props.isOpen ? Colors.border : 'transparent')};
  border-radius: 0 0 ${Shapes.borderRadius}px ${Shapes.borderRadius}px;
  padding: ${(props: InsightFooterProps) => (props.isOpen ? `${Shapes.spacing[4]}px` : 0)};
  height: ${(props: InsightFooterProps) => (props.isOpen ? 'auto' : '0px')};
  overflow: hidden;
`

const InsightFooterContent = styled.div`
  padding: 0 ${Shapes.spacing[3]}px;

  & > p:not(:last-of-type) {
    margin-bottom: ${Shapes.spacing[2]}px;
  }
`

export const InsightCard: React.FC<InsightCardProps> = (props) => {
  const {
    id,
    title,
    left,
    right,
    subheader,
    children,
    noPadding,
    errorMsg,
    errorTitle,
    tooltipFile,
    subtitle,
    noHeaderMargin,
    footerActionLabel = 'Details',
    footer,
    containerPadding,
  } = props

  const [footerOpen, setFooterOpen] = useState(false)
  const FooterActionIcon = footerOpen ? KeyboardArrowUp : KeyboardArrowDown

  const onFooterActionClick = () => {
    setFooterOpen(!footerOpen)
  }

  return (
    <InsightCardPrintWrapper id={id} className={INSIGHT_WIDGET_PRINTABLE_CLASS}>
      <InsightCardWrapper isFooterOpen={!!footer && footerOpen} padding={containerPadding}>
        <InsightCardHeader style={noHeaderMargin ? { marginBottom: `${Shapes.spacing[2]}px` } : {}}>
          <InsightCardHeaderLeft>
            <InsightCardHeaderTitle>
              <Typography variant="title1" color={Colors.textBlack}>
                {title}
              </Typography>
              {subtitle}
            </InsightCardHeaderTitle>
            <InsightCardHeaderLeftExtra>{left}</InsightCardHeaderLeftExtra>
          </InsightCardHeaderLeft>
          <InsightCardHeaderRight>
            <InsightCardHeaderRightExtra>{right}</InsightCardHeaderRightExtra>
            {tooltipFile && (
              <HelpTooltip file={tooltipFile} placement="bottom" immediate>
                <InsightCardHeaderRightBtn aria-label="Help" className="noprint">
                  <InsightHelpCircleIcon />
                </InsightCardHeaderRightBtn>
              </HelpTooltip>
            )}
          </InsightCardHeaderRight>
        </InsightCardHeader>
        {subheader && <InsightCardSubHeader>{subheader}</InsightCardSubHeader>}
        <InsightCardContent noPadding={noPadding} className="content">
          <InsightCardLoadingBoundary title={errorTitle} message={errorMsg}>
            {children}
          </InsightCardLoadingBoundary>
          {footer && (
            <InsightFooterAction onClick={onFooterActionClick} className="noprint">
              <InsightFooterActionBtn>
                <Typography variant="text1" color={Colors.blueMedium}>
                  {footerActionLabel}
                </Typography>
                <FooterActionIcon size={15} color={Colors.blueMedium} />
              </InsightFooterActionBtn>
            </InsightFooterAction>
          )}
        </InsightCardContent>
      </InsightCardWrapper>
      {footer && (
        <InsightFooter isOpen={footerOpen} className="noprint">
          <InsightFooterContent>{footer}</InsightFooterContent>
        </InsightFooter>
      )}
    </InsightCardPrintWrapper>
  )
}
